// RecipeCard.tsx — Compact three-slot card for the recipe listing page.
// Header carries the title + difficulty, Body the summary and measured meta,
// Footer the navigation action and category (DESIGN.md C-CARD-1).
import Card from 'react-bootstrap/Card'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import { BsRulers, BsClock, BsListOl, BsArrowRight } from 'react-icons/bs'
import { useNavigate } from 'react-router-dom'
import { formatMass, formatMinutes } from '../format'

interface RecipeCardProps {
    id: string
    title: string
    subtitle: string
    difficulty: 'beginner' | 'intermediate' | 'advanced' | 'expert'
    totalMassG: number
    totalTimeMin: number
    category: string
    stepCount: number
    /** True when the signed-in user owns this recipe; shows a "yours" marker. */
    canEdit?: boolean
}

// Difficulty maps onto Bootstrap's semantic scale: green through red as the
// procedure gets less forgiving. These are badges — the one place a semantic
// fill is still allowed — and Bootswatch leaves info/warning/danger pale
// enough to need a dark foreground (DESIGN.md C-MSG-2).
export const difficultyBadge: Record<string, { bg: string; text?: string }> = {
    beginner: { bg: 'success' },
    intermediate: { bg: 'info', text: 'dark' },
    advanced: { bg: 'warning', text: 'dark' },
    expert: { bg: 'danger', text: 'dark' },
}

const RecipeCard: React.FC<RecipeCardProps> = ({
    id, title, subtitle, difficulty, totalMassG, totalTimeMin, category, stepCount, canEdit,
}) => {
    const navigate = useNavigate()
    const badge = difficultyBadge[difficulty]

    return (
        <Card
            className="recipe-card"
            role="button"
            onClick={() => navigate(`/recipe/${id}`)}
        >
            <Card.Header className="d-flex justify-content-between align-items-center gap-2">
                <span className="text-truncate">{title}</span>
                <Badge bg={badge.bg} text={badge.text} className="text-uppercase flex-shrink-0">
                    {difficulty}
                </Badge>
            </Card.Header>

            <Card.Body className="d-flex flex-column">
                <p className="text-body-secondary small flex-grow-1">{subtitle}</p>
                {/* Each readout is one unbreakable unit: a mass split from its
                    unit, or a count from its noun, reads as two facts. The row
                    wraps between items rather than inside one. */}
                <Stack
                    direction="horizontal"
                    className="flex-wrap justify-content-between column-gap-3 row-gap-1 small text-body-secondary"
                >
                    <span title="Total ingredient mass" className="d-flex align-items-center gap-1 text-nowrap">
                        <BsRulers /> {formatMass(totalMassG)}
                    </span>
                    <span title="Estimated total time" className="d-flex align-items-center gap-1 text-nowrap">
                        <BsClock /> {formatMinutes(totalTimeMin)}
                    </span>
                    <span title="Number of procedure steps" className="d-flex align-items-center gap-1 text-nowrap">
                        <BsListOl /> {stepCount} {stepCount === 1 ? 'step' : 'steps'}
                    </span>
                </Stack>
            </Card.Body>

            <Card.Footer className="d-flex justify-content-between align-items-center gap-2">
                <Button variant="link" size="sm" className="p-0 d-flex align-items-center gap-1">
                    View Recipe <BsArrowRight />
                </Button>
                <span className="d-flex align-items-center gap-2">
                    {canEdit && <Badge bg="primary">yours</Badge>}
                    {category && <Badge bg="secondary">{category}</Badge>}
                </span>
            </Card.Footer>
        </Card>
    )
}

export default RecipeCard
